import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { FormControl, FormControlProps } from 'react-bootstrap';
import { Fade } from 'components/Transition';
import useMediaQuery from 'hooks/useMediaQuery';
import classlist from 'utils/classlist';
import { getLabelsWithSelectedOptions, getOptionsWithValues } from './helper';
import { openPickerModal, PickerModalProps } from './PickerModal';
import styles from './style.scss';
import { MobileContentProps, OptionType, OptionValueType, PanelContentProps } from './type';

export interface PickerProps
    extends Omit<FormControlProps, 'onChange' | 'value' | 'defaultValue'>,
        Pick<PickerModalProps, 'hideHeader'> {
    disabled?: boolean;
    className?: string;
    // 用于自定义输入框样式
    inputClassName?: string;
    value?: OptionValueType;
    defaultValue?: OptionValueType;
    /** 用于自定义移动端弹层内容 */
    mobileComponent?: (props: MobileContentProps) => ReactNode;
    /** 用于自定义PC端面板内容 */
    panelComponent?: (props: PanelContentProps) => ReactNode;
    options: OptionType[];
    onChange?: (value: OptionValueType) => void;
    /** 当option的label是复杂的ReactNode时，可自定义选中后输入框控件展示的文案渲染 */
    labelRender?: (selectedOptions: OptionType[]) => string;
    /** 下拉面板是否和输入框控件等宽，默认为true */
    panelMatchInputWidth?: boolean;
    isInvalid?: boolean;
}

const Picker: FC<PickerProps> = ({
    className,
    inputClassName,
    disabled,
    value,
    defaultValue,
    options = [],
    mobileComponent,
    panelComponent,
    onChange,
    labelRender,
    hideHeader,
    isInvalid,
    panelMatchInputWidth = true,
    ...restProps
}) => {
    const [curValue, setCurValue] = useState<OptionValueType>(
        typeof value === 'undefined' ? defaultValue || [] : value
    );
    const { selectedLabels, selectedOptions } = useMemo(() => {
        const selectedOptions = getOptionsWithValues(curValue, options);
        const selectedLabels = getLabelsWithSelectedOptions(selectedOptions);

        return {
            selectedLabels,
            selectedOptions
        };
    }, [curValue, options]);
    const rootRef = useRef<HTMLDivElement>(null);
    const [panelVisible, setPanelVisible] = useState<boolean>(false);
    const isSmScreen = useMediaQuery();

    const handleClick = () => {
        if (isSmScreen) {
            openPickerModal({ value: curValue, options, contentRender: mobileComponent, hideHeader }).then(
                (value: OptionValueType) => {
                    if (value) {
                        handleChange(value);
                    }
                }
            );
        } else {
            setPanelVisible(val => !val);
        }
    };

    const handleChange = (value: OptionValueType, close?: boolean) => {
        setCurValue(value);
        onChange?.(value);
        close && setPanelVisible(false);
    };

    useEffect(() => {
        setCurValue(value || []);
    }, [value]);

    useEffect(() => {
        const onClick = (ev: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(ev.target as HTMLDivElement)) {
                setPanelVisible(false);
            }
        };

        if (isSmScreen) {
            setPanelVisible(false);
        } else {
            window.addEventListener('click', onClick);
        }

        return () => {
            window.removeEventListener('click', onClick);
        };
    }, [isSmScreen]);

    return (
        <div className={classlist(styles.root, className)} ref={rootRef}>
            <FormControl
                {...restProps}
                className={classlist(inputClassName, {
                    'is-invalid': isInvalid
                })}
                readOnly
                onClick={handleClick}
                onChange={handleClick}
                disabled={disabled}
                value={labelRender ? labelRender(selectedOptions) : selectedLabels?.join(' / ')}
            />
            <Fade in={panelVisible} timeout={200}>
                <div
                    className={classlist(styles.panel, {
                        [styles.panelMatchInputWidth]: panelMatchInputWidth
                    })}>
                    {panelComponent && panelComponent({ value: curValue, options, onChange: handleChange })}
                </div>
            </Fade>
        </div>
    );
};

export default Picker;
export * from './type';
export * from './helper';
